import { fetchSignatureAssetBase64, type SignatureAssetAuth } from "../helpers/signature-assets-api";
import { hexToGuid, recoverAssetMarkersIn } from "./signatureAssetImages";
import { parseHtmlInert } from "./inertHtml";

const IMG_TAG = /<img\b[^>]*>/gi;
/** cid:sunnysign-<32 Hex>.<ext> — daraus lässt sich die Asset-ID auch ohne Marker-Attribut ableiten. */
const CID_ASSET_SRC = /^cid:(sunnysign-([0-9a-f]{32})\.(?:png|jpe?g|gif|webp))$/i;

type AttachedAsset = { fileName: string; contentType: string };
type AssetRef = { assetId: string; fileName: string };

export type InlineAttachmentOutcome = {
  html: string;
  /** Anzahl unterschiedlicher Attachment-Bilder im HTML. */
  expected: number;
  /** Davon erfolgreich als Inline-Anhang gesetzt. */
  attached: number;
};

/**
 * Die Launch-Runtime von Outlook Classic (autorunweb.js) ist reines JavaScript ohne DOM.
 * Dort greift der Regex-Zweig, sonst der DOM-Zweig — analog zu canUseDomForHtmlPrep().
 */
function canUseDom(): boolean {
  try {
    return typeof document !== "undefined" && typeof document.createElement === "function";
  } catch {
    return false;
  }
}

function fallbackFileName(assetId: string): string {
  return `sunnysign-${assetId.replace(/-/g, "")}.png`;
}

/** Namen der bereits am Entwurf hängenden Anhänge (leer, wenn die Abfrage nicht unterstützt wird). */
async function getExistingAttachmentNames(mailItem: Office.MessageCompose): Promise<Set<string>> {
  const names = new Set<string>();
  if (typeof mailItem.getAttachmentsAsync !== "function") return names;
  try {
    const attachments = await new Promise<Office.AttachmentDetailsCompose[]>((resolve, reject) => {
      mailItem.getAttachmentsAsync!((r) => {
        if (r.status === Office.AsyncResultStatus.Succeeded) resolve(r.value ?? []);
        else reject(r.error ?? new Error("get_attachments_failed"));
      });
    });
    for (const a of attachments) {
      if (a.name) names.add(a.name);
    }
  } catch (e) {
    console.warn("[SunnySign] getAttachmentsAsync fehlgeschlagen:", e);
  }
  return names;
}

/** Lädt die Assets und hängt sie inline an; liefert je Asset den tatsächlich verwendeten Dateinamen. */
async function attachAssets(
  refs: AssetRef[],
  mailItem: Office.MessageCompose,
  auth?: SignatureAssetAuth
): Promise<Map<string, AttachedAsset>> {
  const resolved = new Map<string, AttachedAsset>();
  const attachedNames = await getExistingAttachmentNames(mailItem);

  for (const ref of refs) {
    try {
      const asset = await fetchSignatureAssetBase64(ref.assetId, auth);
      const name = asset.fileName || ref.fileName;
      // Autorun und manuelles Einfügen können dieselbe Datei anhängen wollen.
      if (attachedNames.has(name)) {
        console.log("[SunnySign] Inline-Attachment bereits vorhanden:", name);
      } else {
        await new Promise<void>((resolve, reject) => {
          mailItem.addFileAttachmentFromBase64Async!(asset.base64, name, { isInline: true }, (r) => {
            if (r.status === Office.AsyncResultStatus.Succeeded) resolve();
            else reject(r.error ?? new Error("attach_failed"));
          });
        });
        attachedNames.add(name);
      }
      resolved.set(ref.assetId, { fileName: name, contentType: asset.contentType || "" });
    } catch (e) {
      console.warn("[SunnySign] Inline-Attachment fehlgeschlagen für", ref.assetId, e);
    }
  }

  return resolved;
}

/**
 * Führendes Trennzeichen statt \b: `\bsrc=` würde sonst auch in `data-src=` greifen.
 * Kein Lookbehind — die Classic-Runtime läuft im Produktionsmodus nicht auf V8.
 */
function attrPattern(name: string): RegExp {
  return new RegExp(`(^|[\\s"'])${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i");
}

function readTagAttr(tag: string, name: string): string {
  const m = attrPattern(name).exec(tag);
  if (!m) return "";
  return (m[2] ?? m[3] ?? m[4] ?? "").trim();
}

function writeTagAttr(tag: string, name: string, value: string): string {
  const escaped = value.replace(/"/g, "&quot;");
  const pattern = attrPattern(name);
  if (pattern.test(tag)) {
    return tag.replace(pattern, (_full, lead: string) => `${lead}${name}="${escaped}"`);
  }
  return tag.replace(/^<img/i, `<img ${name}="${escaped}"`);
}

/** Asset-Bezug eines img-Tags: bevorzugt der Marker, ersatzweise aus dem cid:-Dateinamen rekonstruiert. */
function assetRefFromTag(tag: string): AssetRef | null {
  const marker = readTagAttr(tag, "data-sunnysign-asset");
  const cid = CID_ASSET_SRC.exec(readTagAttr(tag, "src"));
  const assetId = marker || (cid ? hexToGuid(cid[2].toLowerCase()) : "");
  if (!assetId) return null;
  const fileName =
    readTagAttr(tag, "data-sunnysign-filename") || (cid ? cid[1] : fallbackFileName(assetId));
  return { assetId, fileName };
}

function uniqueRefs(refs: AssetRef[]): AssetRef[] {
  const byId = new Map<string, AssetRef>();
  for (const ref of refs) {
    if (!byId.has(ref.assetId)) byId.set(ref.assetId, ref);
  }
  return Array.from(byId.values());
}

async function resolveWithoutDom(
  html: string,
  mailItem: Office.MessageCompose,
  auth?: SignatureAssetAuth
): Promise<InlineAttachmentOutcome> {
  const refs = uniqueRefs(
    (html.match(IMG_TAG) ?? []).map(assetRefFromTag).filter((r): r is AssetRef => r !== null)
  );
  if (refs.length === 0) return { html, expected: 0, attached: 0 };

  const resolved = await attachAssets(refs, mailItem, auth);
  if (resolved.size === 0) return { html, expected: refs.length, attached: 0 };

  const out = html.replace(IMG_TAG, (tag) => {
    const ref = assetRefFromTag(tag);
    const asset = ref ? resolved.get(ref.assetId) : undefined;
    if (!ref || !asset) return tag;
    let next = writeTagAttr(tag, "src", `cid:${asset.fileName}`);
    next = writeTagAttr(next, "data-sunnysign-asset", ref.assetId);
    next = writeTagAttr(next, "data-sunnysign-filename", asset.fileName);
    if (asset.contentType) next = writeTagAttr(next, "data-sunnysign-ctype", asset.contentType);
    return next;
  });
  return { html: out, expected: refs.length, attached: resolved.size };
}

async function resolveWithDom(
  html: string,
  mailItem: Office.MessageCompose,
  auth?: SignatureAssetAuth
): Promise<InlineAttachmentOutcome> {
  const wrap = parseHtmlInert(html);
  recoverAssetMarkersIn(wrap);
  const imgs = Array.from(wrap.querySelectorAll("img[data-sunnysign-asset]")) as HTMLImageElement[];

  const refs = uniqueRefs(
    imgs
      .map((img) => {
        const assetId = img.getAttribute("data-sunnysign-asset")?.trim();
        if (!assetId) return null;
        const fileName =
          img.getAttribute("data-sunnysign-filename")?.trim() || fallbackFileName(assetId);
        return { assetId, fileName };
      })
      .filter((r): r is AssetRef => r !== null)
  );
  if (refs.length === 0) return { html, expected: 0, attached: 0 };

  const resolved = await attachAssets(refs, mailItem, auth);
  if (resolved.size === 0) return { html, expected: refs.length, attached: 0 };

  for (const img of imgs) {
    const assetId = img.getAttribute("data-sunnysign-asset")?.trim();
    const asset = assetId ? resolved.get(assetId) : undefined;
    if (!asset) continue;
    img.setAttribute("src", `cid:${asset.fileName}`);
    img.setAttribute("data-sunnysign-filename", asset.fileName);
    if (asset.contentType) img.setAttribute("data-sunnysign-ctype", asset.contentType);
  }
  return { html: wrap.innerHTML, expected: refs.length, attached: resolved.size };
}

/**
 * Hängt die Bilder mit data-sunnysign-asset als Inline-Anhänge an und setzt src auf cid:dateiname.
 * `auth` ist nur dort nötig, wo getAccessToken() nichts findet (Outlook Classic Launch-Runtime).
 */
export async function resolveInlineAttachmentsForComposeDetailed(
  html: string,
  mailItem: Office.MessageCompose | undefined | null,
  auth?: SignatureAssetAuth
): Promise<InlineAttachmentOutcome> {
  if (!html || !mailItem) return { html: html || "", expected: 0, attached: 0 };
  if (typeof mailItem.addFileAttachmentFromBase64Async !== "function") {
    console.warn(
      "[SunnySign] addFileAttachmentFromBase64Async fehlt — Attachment-Bilder bleiben als cid: ohne Anhang"
    );
    return { html, expected: 0, attached: 0 };
  }

  const outcome = canUseDom()
    ? await resolveWithDom(html, mailItem, auth)
    : await resolveWithoutDom(html, mailItem, auth);

  if (outcome.expected > 0) {
    const summary = `[SunnySign] Inline-Attachments: ${outcome.attached}/${outcome.expected} angehängt (${canUseDom() ? "DOM" : "kein DOM"})`;
    if (outcome.attached < outcome.expected) console.warn(summary);
    else console.log(summary);
  }
  return outcome;
}

export async function resolveInlineAttachmentsForCompose(
  html: string,
  mailItem: Office.MessageCompose | undefined | null,
  auth?: SignatureAssetAuth
): Promise<string> {
  return (await resolveInlineAttachmentsForComposeDetailed(html, mailItem, auth)).html;
}
